import { loggerManager } from '../Logger/LoggerManager'

export type EventType = string | symbol

/**
 * Базовий обробник подій
 */
export type Handler<T = any> = (event: T, context?: any) => any

/**
 * Обробник wildcard подій
 */
export type WildcardHandler = (type: EventType, event: any, context?: any) => any

/**
 * Іменований обробник з опціями
 */
export interface NamedHandler {
    name: string
    handler: Handler | WildcardHandler
    options?: HandlerOptions
    isWildcard?: boolean
}

/**
 * Опції для обробників
 */
export interface HandlerOptions {
    once?: boolean
    priority?: number
}

/**
 * Результат виконання події
 */
export interface EventResult<T = any> {
    handlerName: string
    result: T
    error?: Error
}

/**
 * Конфігурація EventManager
 */
export interface EventManagerConfig {
    context?: any
    debug?: boolean
    maxRecursionDepth?: number
}

/**
 * Сучасний менеджер подій для нового ядра системи
 * Поєднує найкращі можливості зі старого EventManager та mitt
 */
export class EventManager {
    private readonly handlers: Map<EventType, NamedHandler[]>
    private readonly activeEvents: Set<EventType>
    private readonly context: any
    private readonly debug: boolean
    private readonly maxRecursionDepth: number
    private recursionDepth: number = 0

    constructor(config: EventManagerConfig = {}) {
        this.handlers = new Map()
        this.activeEvents = new Set()
        this.context = config.context
        this.debug = config.debug ?? false
        this.maxRecursionDepth = config.maxRecursionDepth ?? 10
    }

    /**
     * Реєструє іменований обробник події
     */
    on(
        eventName: EventType,
        handlerName: string,
        handler: Handler | WildcardHandler,
        options: HandlerOptions = {}
    ): void {
        if (typeof eventName !== 'string' && typeof eventName !== 'symbol') {
            this.warn('Event name must be string or symbol')
            return
        }

        if (typeof handlerName !== 'string') {
            this.warn('Handler name must be string')
            return
        }

        if (typeof handler !== 'function') {
            this.warn('Handler must be function')
            return
        }

        const namedHandler: NamedHandler = {
            name: handlerName,
            handler,
            options,
            isWildcard: eventName === '*',
        }

        const existingHandlers = this.handlers.get(eventName) || []

        /** Видаляємо існуючий обробник з такою ж назвою */
        const filteredHandlers = existingHandlers.filter((h) => h.name !== handlerName)

        /** Додаємо новий обробник з урахуванням пріоритету */
        filteredHandlers.push(namedHandler)

        /** Сортуємо за пріоритетом (вищий пріоритет першим) */
        filteredHandlers.sort((a, b) => (b.options?.priority ?? 0) - (a.options?.priority ?? 0))

        this.handlers.set(eventName, filteredHandlers)

        this.log(`Handler '${handlerName}' registered for event '${String(eventName)}'`)
    }

    /**
     * Видаляє обробник події
     */
    off(eventName: EventType, handlerName?: string): void {
        if (typeof eventName !== 'string' && typeof eventName !== 'symbol') {
            this.warn('Event name must be string or symbol')
            return
        }

        const existingHandlers = this.handlers.get(eventName)
        if (!existingHandlers) {
            this.log(`No handlers found for event '${String(eventName)}'`)
            return
        }

        if (!handlerName) {
            /** Видаляємо всі обробники для події */
            this.handlers.set(eventName, [])
            this.log(`All handlers removed for event '${String(eventName)}'`)
            return
        }

        /** Видаляємо конкретний обробник */
        const filteredHandlers = existingHandlers.filter((h) => h.name !== handlerName)
        this.handlers.set(eventName, filteredHandlers)

        this.log(`Handler '${handlerName}' removed from event '${String(eventName)}'`)
    }

    /**
     * Синхронне виконання події
     */
    emit(eventName: EventType, event?: any): void {
        
        if (this.activeEvents.has(eventName)) {
            this.warn(`Recursive event detected: ${String(eventName)}`)
            return
        }

        if (this.recursionDepth >= this.maxRecursionDepth) {
            this.warn(`Maximum recursion depth reached: ${this.maxRecursionDepth}`)
            return
        }

        this.activeEvents.add(eventName)
        this.recursionDepth++

        try {
            this.log(`Emitting event: ${String(eventName)}`)

            /** Виконуємо обробники для конкретної події */
            const eventHandlers = this.handlers.get(eventName) || []

            this.executeHandlers(eventHandlers, event, eventName)

            /** Виконуємо wildcard обробники */
            const wildcardHandlers = this.handlers.get('*') || []
            this.executeWildcardHandlers(wildcardHandlers, eventName, event)
        } finally {
            this.activeEvents.delete(eventName)
            this.recursionDepth--
        }
    }

    /**
     * Асинхронне виконання події з поверненням результатів
     */
    async emitAsync(eventName: EventType, event?: any): Promise<EventResult[]> {
        if (this.activeEvents.has(eventName)) {
            this.warn(`Recursive event detected: ${String(eventName)}`)
            return []
        }

        if (this.recursionDepth >= this.maxRecursionDepth) {
            this.warn(`Maximum recursion depth reached: ${this.maxRecursionDepth}`)
            return []
        }

        this.activeEvents.add(eventName)
        this.recursionDepth++

        try {
            this.log(`Emitting async event: ${String(eventName)}`)

            const results: EventResult[] = []

            /** Виконуємо обробники для конкретної події */
            const eventHandlers = this.handlers.get(eventName) || []
            const eventResults = await this.executeHandlersAsync(eventHandlers, event, eventName)
            results.push(...eventResults)

            /** Виконуємо wildcard обробники */
            const wildcardHandlers = this.handlers.get('*') || []
            const wildcardResults = await this.executeWildcardHandlersAsync(wildcardHandlers, eventName, event)
            results.push(...wildcardResults)

            return results
        } finally {
            this.activeEvents.delete(eventName)
            this.recursionDepth--
        }
    }

    /**
     * Перевіряє наявність обробників для події
     */
    hasHandlers(eventName: EventType): boolean {
        const handlers = this.handlers.get(eventName)
        return handlers ? handlers.length > 0 : false
    }

    /**
     * Повертає список усіх зареєстрованих подій
     */
    getEventNames(): EventType[] {
        return Array.from(this.handlers.keys())
    }

    /**
     * Повертає список обробників для події
     */
    getHandlers(eventName: EventType): string[] {
        const handlers = this.handlers.get(eventName) || []
        return handlers.map((h) => h.name)
    }

    /**
     * Очищає всі обробники
     */
    clear(): void {
        this.handlers.clear()
        this.activeEvents.clear()
        this.log('All handlers cleared')
    }

    /**
     * Синхронне виконання звичайних обробників
     */
    private executeHandlers(handlers: NamedHandler[], event: any, eventName: EventType): void {
        const handlersToRemove: string[] = []

        for (const namedHandler of handlers) {
            try {
                const handler = namedHandler.handler as Handler
                if (this.context) {
                    handler.call(this.context, event, this.context)
                } else {
                    handler(event, this.context)
                }

                if (namedHandler.options?.once) {
                    handlersToRemove.push(namedHandler.name)
                }
            } catch (error) {
                this.warn(`Error in handler '${namedHandler.name}' for event '${String(eventName)}':`, error)
            }
        }

        /** Видаляємо одноразові обробники */
        for (const handlerName of handlersToRemove) {
            this.off(eventName, handlerName)
        }
    }

    /**
     * Синхронне виконання wildcard обробників
     */
    private executeWildcardHandlers(handlers: NamedHandler[], eventName: EventType, event: any): void {
        const handlersToRemove: string[] = []

        for (const namedHandler of handlers) {
            try {
                const wildcardHandler = namedHandler.handler as WildcardHandler
                if (this.context) {
                    wildcardHandler.call(this.context, eventName, event, this.context)
                } else {
                    wildcardHandler(eventName, event, this.context)
                }

                if (namedHandler.options?.once) {
                    handlersToRemove.push(namedHandler.name)
                }
            } catch (error) {
                this.warn(`Error in wildcard handler '${namedHandler.name}':`, error)
            }
        }

        /** Видаляємо одноразові обробники */
        for (const handlerName of handlersToRemove) {
            this.off('*', handlerName)
        }
    }

    /**
     * Асинхронне виконання звичайних обробників
     */
    private async executeHandlersAsync(
        handlers: NamedHandler[],
        event: any,
        eventName: EventType
    ): Promise<EventResult[]> {
        const results: EventResult[] = []
        const handlersToRemove: string[] = []

        for (const namedHandler of handlers) {
            try {
                const handler = namedHandler.handler as Handler
                let result: any
                if (this.context) {
                    result = await handler.call(this.context, event, this.context)
                } else {
                    result = await handler(event, this.context)
                }

                results.push({
                    handlerName: namedHandler.name,
                    result,
                })

                if (namedHandler.options?.once) {
                    handlersToRemove.push(namedHandler.name)
                }
            } catch (error) {
                this.warn(`Error in handler '${namedHandler.name}' for event '${String(eventName)}':`, error)
                results.push({
                    handlerName: namedHandler.name,
                    result: undefined,
                    error: error as Error,
                })
            }
        }

        /** Видаляємо одноразові обробники */
        for (const handlerName of handlersToRemove) {
            this.off(eventName, handlerName)
        }

        return results
    }

    /**
     * Асинхронне виконання wildcard обробників
     */
    private async executeWildcardHandlersAsync(
        handlers: NamedHandler[],
        eventName: EventType,
        event: any
    ): Promise<EventResult[]> {
        const results: EventResult[] = []
        const handlersToRemove: string[] = []

        for (const namedHandler of handlers) {
            try {
                const wildcardHandler = namedHandler.handler as WildcardHandler
                let result: any
                if (this.context) {
                    result = await wildcardHandler.call(this.context, eventName, event, this.context)
                } else {
                    result = await wildcardHandler(eventName, event, this.context)
                }

                results.push({
                    handlerName: namedHandler.name,
                    result,
                })

                if (namedHandler.options?.once) {
                    handlersToRemove.push(namedHandler.name)
                }
            } catch (error) {
                this.warn(`Error in wildcard handler '${namedHandler.name}':`, error)
                results.push({
                    handlerName: namedHandler.name,
                    result: undefined,
                    error: error as Error,
                })
            }
        }

        /** Видаляємо одноразові обробники */
        for (const handlerName of handlersToRemove) {
            this.off('*', handlerName)
        }

        return results
    }

    /**
     * Логування налагоджувальних повідомлень
     */
    private log(message: string, ...args: any[]): void {
        if (this.debug) {
            loggerManager.debug(message, 'event', undefined, args.length > 0 ? args : undefined)
        }
    }

    /**
     * Логування попереджень
     */
    private warn(message: string, ...args: any[]): void {
        if (this.debug) {
            loggerManager.warn(message, 'event', undefined, args.length > 0 ? args : undefined)
        }
    }
}

/**
 * Фабрична функція для створення екземпляра EventManager
 */
export function createEventManager(config?: EventManagerConfig): EventManager {
    return new EventManager(config)
}

/**
 * Експорт за замовчуванням
 */
export default EventManager
